import React, { useMemo } from 'react';

import { Box, Button, Chip, Stack, Typography } from '@mui/material';

import { RateGroupRule } from '~/services/cruises/RateGroupRuleService';
import { RateGroup } from '~/services/cruises/RateGroupService';

const FILTER_TYPE_LABELS = {
  RATE_CODE: 'Rate Code',
  RATE_CODE_DESCRIPTION: 'Rate Code Description',
};

interface Props {
  rateGroup: RateGroup;
  rateGroupRules: Array<RateGroupRule>;
  handleEdit: (rateGroupRule: RateGroupRule) => void;
  handleDelete: (rateGroupRule: RateGroupRule) => void;
}

function RateGroupRulesItemByGroup({ rateGroup, rateGroupRules, handleEdit, handleDelete }: Props) {
  const rateRules = useMemo(() => {
    return rateGroupRules.filter((rule) => rule.rateGroupId === rateGroup.id);
  }, [rateGroupRules, rateGroup]);

  return (
    <Stack direction="column" spacing={2}>
      <Typography variant="h6">
        {rateGroup.name} {rateGroup.isRefundable ? '(Refundable)' : '(Non-Refundable)'}
      </Typography>

      <Stack direction="column" spacing={1}>
        {rateRules.map((rateGroupRule, index) => (
          <Box key={rateGroupRule.id} sx={{ position: 'relative' }}>
            <Stack
              direction="row"
              spacing={3}
              alignItems="center"
              alignContent="space-between"
              sx={{ borderColor: 'divider', borderWidth: 1, borderStyle: 'solid', padding: 1 }}
            >
              <Typography variant="h6" sx={{ minWidth: '150px' }}>
                Rule {index + 1}
              </Typography>

              <Stack
                direction="column"
                spacing={1}
                sx={{ borderLeft: '1px solid', borderColor: 'divider', padding: 1, width: '100%' }}
              >
                {rateGroupRule.filters?.map((filter, index) => (
                  <Box key={rateGroupRule.id} sx={{ position: 'relative' }}>
                    <Box sx={{ border: '1px solid', borderColor: 'divider', padding: 2 }}>
                      <Typography>
                        <b>
                          {FILTER_TYPE_LABELS[filter.filterType]} - {filter.matchType}
                        </b>
                        : {filter.rateCodePattern}
                      </Typography>
                    </Box>
                    {rateGroupRule.filters.length > 1 && index < rateGroupRule.filters.length - 1 && (
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ position: 'absolute', left: 0, bottom: 0, transform: 'translateY(50%)' }}
                      >
                        <Chip label="AND" color="primary" size="small" />
                      </Stack>
                    )}
                  </Box>
                ))}
              </Stack>

              <Stack direction="row" spacing={1} sx={{ marginLeft: 'auto' }}>
                <Button size="small" onClick={() => handleEdit(rateGroupRule)}>
                  Edit
                </Button>

                <Button size="small" color="error" onClick={() => handleDelete(rateGroupRule)}>
                  Delete
                </Button>
              </Stack>
            </Stack>

            {rateRules.length > 1 && index < rateRules.length - 1 && (
              <Stack
                direction="row"
                spacing={1}
                sx={{ position: 'absolute', left: 0, bottom: 0, transform: 'translateY(50%)' }}
              >
                <Chip label="OR" color="primary" />
              </Stack>
            )}
          </Box>
        ))}
      </Stack>

      {!rateRules.length && (
        <Typography variant="body1" sx={{ color: 'text.secondary' }} align="center">
          No rate group rules created
        </Typography>
      )}
    </Stack>
  );
}

export default RateGroupRulesItemByGroup;
